import React, { useEffect, useRef, useState } from 'react';
import ReportSpecificStrategiesPanel from '../report/ReportSpecificStrategiesPanel';
import SkillCodeNameTooltip from '../report/SkillCodeNameTooltip';
import './SpecificStrategiesSidebarPanel.css';

const MI_PROCESSES = [
  { value: 'Evoking', shortLabel: 'EV' },
];
const STRATEGIES = [{ value: 'Scaling Questions', shortLabel: 'SQ' }];

const MITI_CODE_CLASS = {
  Q: 'miti-chip--Q', OQ: 'miti-chip--Q', CQ: 'miti-chip--Q', S: 'miti-chip--S',
  AF: 'miti-chip--AF', SR: 'miti-chip--SR', CR: 'miti-chip--CR', Seek: 'miti-chip--Seek',
  GI: 'miti-chip--GI', Persuade: 'miti-chip--Persuade', Persuade_with: 'miti-chip--Persuade_with',
  Emphasize: 'miti-chip--Emphasize', Confront: 'miti-chip--Confront', NC: 'miti-chip--NC',
};

const MITI_DISPLAY_NAMES = {
  SR: 'Simple Reflection', CR: 'Complex Reflection', OQ: 'Open-Ended Question',
  CQ: 'Closed Question', Q: 'Question', S: 'Summary', AF: 'Affirmation',
  GI: 'Giving Information', Seek: 'Seeking Collaboration', Persuade: 'Persuade',
  Persuade_with: 'Persuade with Permission', Emphasize: 'Emphasize Autonomy',
  Confront: 'Confront', NC: 'Not Coded',
};

function StrategySidebarMitiChip({ label }) {
  const codeClass = MITI_CODE_CLASS[label] ?? 'miti-chip--NC';
  const fullName = MITI_DISPLAY_NAMES[label] ?? label;
  const chip = (
    <span className={`miti-chip ${codeClass}`} aria-label={fullName}>
      {label}
    </span>
  );
  return fullName === label
    ? chip
    : <SkillCodeNameTooltip name={fullName}>{chip}</SkillCodeNameTooltip>;
}

function SpecificStrategiesSidebarPanel({
  WheelArrowUp,
  WheelArrowDown,
  showGuidedHighlights = true,
  selectedTask,
  selectedStrategy,
  exerciseStatus = 'idle',
  onTaskChange,
  onStrategyChange,
  showDrawerDetails = false,
  theme = 'dark',
  t,
  strategiesFlow,
  onTryAgain,
  onNextExercise,
  isReplay = false,
  onReturnToAccount,
  onShowAutomatedMiscMitiCounsellor,
}) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [completionCueVisible, setCompletionCueVisible] = useState(false);
  const [feedbackScrolled, setFeedbackScrolled] = useState(false);
  const panelRef = useRef(null);
  const stimulusTurnCount = strategiesFlow?.exercise?.turn_count || 0;
  const submittedTurnCount = (strategiesFlow?.conversation || []).filter(
    (turn, index) => index >= stimulusTurnCount && turn.speaker === 'counsellor',
  ).length;
  const showDrawerActionDock = (
    showDrawerDetails
    && Boolean(strategiesFlow?.exercise)
    && submittedTurnCount > 0
  );
  const drawerStrategiesFlow = showDrawerDetails && strategiesFlow
    ? {
        ...strategiesFlow,
        selectStrategy: () => onNextExercise?.(),
      }
    : strategiesFlow;

  useEffect(() => {
    setCompletionCueVisible(false);
    if (exerciseStatus !== 'complete') return undefined;

    const timeout = window.setTimeout(() => {
      setCompletionCueVisible(true);
    }, 2000);
    return () => window.clearTimeout(timeout);
  }, [exerciseStatus]);

  useEffect(() => {
    setFeedbackScrolled(false);
  }, [strategiesFlow?.attemptId]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const renderDropdown = ({
    id,
    value,
    displayValue = value || '--',
    options,
    disabled = false,
    onSelect,
    highlight = false,
    highlightPurpose,
  }) => {
    const isOpen = openDropdown === id;
    return (
      <div className={`oars-skill-dropdown specific-strategies-dropdown${highlight ? ' client-selection-cue' : ''}`}>
        <div
          className={`wheel-window oars-dropdown-trigger${isOpen ? ' open' : ''}${disabled ? ' disabled' : ''}`}
          onClick={() => {
            if (!disabled) setOpenDropdown(isOpen ? null : id);
          }}
          tabIndex={disabled ? -1 : 0}
          role="button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-disabled={disabled}
          data-guided-highlight-purpose={highlight ? highlightPurpose : undefined}
          onKeyDown={(event) => {
            if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault();
              setOpenDropdown(isOpen ? null : id);
            }
          }}
        >
          <span className="wheel-window-number specific-strategies-dropdown__value">{displayValue}</span>
          <div className="wheel-scroll-track" aria-hidden="true">
            <div className="oars-dropdown-arrow">
              {isOpen ? <WheelArrowUp /> : <WheelArrowDown />}
            </div>
          </div>
        </div>

        {isOpen && (
          <div className="oars-skill-dropdown-menu specific-strategies-dropdown__menu" role="listbox">
            {options.map((option) => {
              const optionValue = typeof option === 'string' ? option : option.value;
              const shortLabel = typeof option === 'string' ? null : option.shortLabel;
              return (
                <button
                  key={optionValue}
                  type="button"
                  role="option"
                  aria-selected={optionValue === value}
                  className={`oars-skill-dropdown-option${optionValue === value ? ' selected' : ''}`}
                  onClick={() => {
                    onSelect?.(optionValue);
                    setOpenDropdown(null);
                  }}
                >
                  {shortLabel && <span className="oars-skill-dropdown-option-letter">{shortLabel}</span>}
                  {optionValue}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="specific-strategies-sidebar oars-sidebar-filters" ref={panelRef}>
      <div className="specific-strategies-sidebar__controls oars-sidebar-controls">
        <div className="selector-label">Select A Strategy</div>
        <div className="client-wheel-picker">
          <div className="wheel-inline-row">
            <span className="wheel-inline-label">Task Selected:</span>
            <div className="wheel-horizontal-control">
              {renderDropdown({
                id: 'process',
                value: selectedTask,
                displayValue: selectedTask === 'Evoking' ? 'EV' : '--',
                options: MI_PROCESSES,
                onSelect: (task) => {
                  onTaskChange?.(task);
                },
                highlight: showGuidedHighlights && (!selectedTask || completionCueVisible),
                highlightPurpose: completionCueVisible
                  ? 'Choose the MI task for your next strategy exercise.'
                  : 'Choose the MI task you want to practise.',
              })}
            </div>
          </div>

          <div className="wheel-inline-row">
            <span className="wheel-inline-label">Strategy Selected:</span>
            <div className="wheel-horizontal-control">
              {renderDropdown({
                id: 'strategy',
                value: selectedStrategy,
                displayValue: selectedStrategy === 'Scaling Questions' ? 'SQ' : '--',
                options: STRATEGIES,
                disabled: !selectedTask,
                onSelect: onStrategyChange,
                highlight: showGuidedHighlights && !!selectedTask && (!selectedStrategy || completionCueVisible),
                highlightPurpose: completionCueVisible
                  ? 'Choose the strategy for your next exercise.'
                  : 'Choose a strategy within the selected MI task.',
              })}
            </div>
          </div>
        </div>
      </div>

      {showDrawerDetails && strategiesFlow?.exercise && submittedTurnCount > 0 && (
        <div
          className={`oars-sidebar-feedback-divider${feedbackScrolled ? ' oars-sidebar-feedback-divider--visible' : ''}`}
          aria-hidden="true"
        />
      )}

      {showDrawerDetails && strategiesFlow?.exercise && (
        <div className="specific-strategies-sidebar__drawer-report">
          <ReportSpecificStrategiesPanel
            t={t}
            theme={theme}
            strategiesFlow={drawerStrategiesFlow}
            MitiChipComponent={StrategySidebarMitiChip}
            onShowAutomatedMiscMitiCounsellor={onShowAutomatedMiscMitiCounsellor}
            sidebarMode
            onSidebarScrollChange={setFeedbackScrolled}
          />
        </div>
      )}

      {showDrawerActionDock && (
        <div className="oars-sidebar-action-dock specific-strategies-sidebar__action-dock">
          <div className="oars-action-bar">
            {strategiesFlow?.status === 'complete' && (
              <button
                type="button"
                className={`oars-btn oars-btn--secondary${completionCueVisible && !isReplay ? ' oars-btn--selection-cue' : ''}`}
                data-guided-highlight-purpose={completionCueVisible && !isReplay ? 'Try the same Scaling Questions exercise again.' : undefined}
                onClick={onTryAgain}
                disabled={strategiesFlow?.loading}
              >
                {t('report_oars_try_again')}
              </button>
            )}
            <div className="oars-next-exercise-wrap">
              <button
                type="button"
                className={`oars-btn oars-btn--primary${completionCueVisible || isReplay ? ' oars-btn--selection-cue' : ''}`}
                data-guided-highlight-purpose={isReplay
                  ? 'Return to your Account history.'
                  : completionCueVisible
                    ? 'Start the next Scaling Questions exercise.'
                    : undefined}
                onClick={isReplay ? onReturnToAccount : onNextExercise}
                disabled={strategiesFlow?.loading}
              >
                {isReplay ? t('account_return_to_account') : 'Next Exercise'}
              </button>
              {!isReplay && strategiesFlow?.status !== 'complete' && (
                <div className="oars-next-exercise-tooltip" role="status">
                  This exercise is still in progress. Choosing Next Exercise will skip it.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SpecificStrategiesSidebarPanel;
